import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { exchangeCodeForTokens, getUserInfo } from '../services/oidcService';
import './LoginPage.css';

const OidcCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setUser, createSession } = useAuth();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);
  
  // Guard to prevent duplicate processing (React Strict Mode double render)
  const hasProcessedRef = useRef(false);
  const hasFetchedUserInfoRef = useRef(false);

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent duplicate processing
      if (hasProcessedRef.current) {
        console.log('[OIDC Callback] ⚠️ Already processed, skipping duplicate call');
        return;
      }
      
      hasProcessedRef.current = true;
      try {
        console.log('[OIDC Callback] Starting callback handling...');
        console.log('[OIDC Callback] Current URL:', window.location.href);
        console.log('[OIDC Callback] Current pathname:', window.location.pathname);
        console.log('[OIDC Callback] Current search:', window.location.search);
        
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        console.log('[OIDC Callback] URL params:', { code: code ? `${code.substring(0, 10)}...` : null, state, errorParam, errorDescription });
        console.log('[OIDC Callback] All URL search params:', Object.fromEntries(searchParams.entries()));

        if (errorParam) {
          const errorMsg = errorDescription || errorParam;
          console.error('[OIDC Callback] OAuth error received:', errorParam, errorMsg);
          setError(`Authentication failed: ${errorMsg}`);
          setLoading(false);
          return;
        }

        if (!code || !state) {
          console.error('[OIDC Callback] Missing required parameters:', { code: !!code, state: !!state });
          setError('Missing authorization code or state');
          setLoading(false);
          return;
        }

        console.log('[OIDC Callback] 🔄 Exchanging authorization code for tokens...');
        // Exchange code for tokens
        const tokens = await exchangeCodeForTokens(code, state);
        console.log('[OIDC Callback] ✅ Tokens received:', {
          hasAccessToken: !!tokens.access_token,
          accessTokenPreview: tokens.access_token ? tokens.access_token.substring(0, 30) + '...' : 'none',
          hasIdToken: !!tokens.id_token,
          hasRefreshToken: !!tokens.refresh_token,
          expiresIn: tokens.expires_in,
          tokenType: tokens.token_type,
          scope: tokens.scope,
          hasUserInfo: !!tokens.user // Backend may include user info in token response
        });

        // Check if user info is already in token response (backend enhancement)
        let userInfo: any = null;
        if (tokens.user) {
          console.log('[OIDC Callback] 👤 User info found in token response:', tokens.user);
          userInfo = {
            sub: tokens.user.userId,
            email: tokens.user.email,
            name: tokens.user.fullName || tokens.user.email,
            preferred_username: tokens.user.preferred_username || tokens.user.email
          };
        }

        // If user info not in token response, fetch from /oidc/me endpoint
        // Use ref guard to prevent duplicate calls
        if (!userInfo && !hasFetchedUserInfoRef.current) {
          console.log('[OIDC Callback] 🔍 Fetching user info from /oidc/me endpoint...');
          hasFetchedUserInfoRef.current = true;
          userInfo = await getUserInfo(tokens.access_token);
          console.log('[OIDC Callback] ✅ User info received:', {
          sub: userInfo.sub,
          email: userInfo.email,
            name: userInfo.name,
            preferred_username: userInfo.preferred_username,
            claims: Object.keys(userInfo)
        });
        } else if (hasFetchedUserInfoRef.current) {
          console.log('[OIDC Callback] ⚠️ User info already fetched, skipping duplicate call');
        }

        // Parse ID token to get user details (fallback)
        const idTokenPayload = parseJwt(tokens.id_token);
        console.log('[OIDC Callback] 📋 ID token payload:', {
          sub: idTokenPayload.sub,
          email: idTokenPayload.email,
          name: idTokenPayload.name,
          aud: idTokenPayload.aud,
          exp: idTokenPayload.exp,
          iat: idTokenPayload.iat
        });

        // Prepare user data for session (prioritize userInfo > idTokenPayload)
        // Extract email from multiple possible sources
        const email = userInfo?.email || 
                     idTokenPayload.email || 
                     userInfo?.preferred_username ||
                     null;
        
        // Extract full name from multiple possible sources
        const fullName = userInfo?.name || 
                        userInfo?.fullName ||
                        idTokenPayload.name ||
                        (userInfo?.given_name && userInfo?.family_name 
                          ? `${userInfo.given_name} ${userInfo.family_name}` 
                          : null) ||
                        (idTokenPayload.given_name && idTokenPayload.family_name
                          ? `${idTokenPayload.given_name} ${idTokenPayload.family_name}`
                          : null) ||
                        userInfo?.preferred_username ||
                        email ||
                        null;

        const userData = {
          userId: userInfo?.sub || idTokenPayload.sub,
          email: email || 'Not available',
          fullName: fullName || 'Not available'
        };

        // Validate user data
        if (!userData.userId) {
          throw new Error('User ID not found in token response');
        }
        
        // Log what we found
        console.log('[OIDC Callback] 📋 User data prepared:', {
          userId: userData.userId,
          email: userData.email,
          fullName: userData.fullName,
          sources: {
            userInfo_email: userInfo?.email,
            userInfo_name: userInfo?.name,
            idToken_email: idTokenPayload.email,
            idToken_name: idTokenPayload.name
          }
        });
        
        if (!email || email === 'Not available') {
          console.warn('[OIDC Callback] ⚠️ Email not found in user info or ID token');
          console.warn('[OIDC Callback] UserInfo keys:', userInfo ? Object.keys(userInfo) : 'null');
          console.warn('[OIDC Callback] ID Token keys:', Object.keys(idTokenPayload));
        }
        
        if (!fullName || fullName === 'Not available') {
          console.warn('[OIDC Callback] ⚠️ Full name not found in user info or ID token');
        }

        console.log('[OIDC Callback] Creating session with user data:', userData);

        // Store OIDC tokens separately (for OIDC-specific operations)
        localStorage.setItem('id_token', tokens.id_token);
        if (tokens.refresh_token) {
          localStorage.setItem('refresh_token', tokens.refresh_token);
        }
        if (tokens.expires_in) {
          const expirationTime = Date.now() + (tokens.expires_in * 1000);
          localStorage.setItem('auth_token_expires_at', expirationTime.toString());
        }

        // Create SSO session via createSession (this will create SSO JWT token)
        // This is required for cross-subdomain SSO with demo apps
        console.log('[OIDC Callback] Calling createSession to create SSO token...');
        await createSession({
          userId: userData.userId,
          email: userData.email,
          fullName: userData.fullName,
          tenantId: 'syberkey_sso_64a4c5', // Default tenant ID
          loginId: idTokenPayload.jti || idTokenPayload.sub // Use JTI or sub as login ID
        });

        console.log('[OIDC Callback] SSO session created successfully');
        console.log('[OIDC Callback] Redirecting to dashboard...');

        // Redirect to dashboard
        navigate('/dashboard', { replace: true });
      } catch (err: any) {
        console.error('[OIDC Callback] ❌ Error during callback handling:', {
          name: err.name,
          message: err.message,
          stack: err.stack?.substring(0, 500),
          response: err.response?.data,
          status: err.response?.status,
          statusText: err.response?.statusText
        });
        
        // Reset ref on error to allow retry
        hasProcessedRef.current = false;
        hasFetchedUserInfoRef.current = false;
        
        // Provide more detailed error messages
        let errorMessage = 'Authentication failed';
        if (err.response?.data?.error_description) {
          errorMessage = err.response.data.error_description;
        } else if (err.response?.data?.error) {
          errorMessage = `Authentication failed: ${err.response.data.error}`;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        console.error('[OIDC Callback] Final error message:', errorMessage);
        setError(errorMessage);
        setLoading(false);
      }
    };

    handleCallback();
  }, [searchParams, navigate, setToken, setUser, createSession]);

  function parseJwt(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return {};
    }
  }

  if (loading) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>Completing authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate('/login')} className="syberkey-login-btn">
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default OidcCallback;

